// ══════════════════════════════════════════════════════════════
// COMPASS – Global Error Handler
// ══════════════════════════════════════════════════════════════
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorReportingService } from '../services/error-reporting.service';
import { Logger } from '../services/logger.service';

/**
 * Application-wide error handler that intercepts unhandled errors,
 * preserves default console logging, and delegates to
 * {@link ErrorReportingService} for structured reporting.
 *
 * HTTP errors are skipped — they are already handled by the
 * auth interceptor and individual service `catchError` blocks.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorReporting = inject(ErrorReportingService);
  private readonly logger = inject(Logger);

  handleError(error: unknown): void {
    // Angular wraps unhandled promise rejections in an Error
    // with a `.rejection` property containing the original value
    const unwrapped = this.unwrapError(error);

    // Skip HttpErrorResponse — already handled by interceptors/services
    if (unwrapped instanceof HttpErrorResponse) {
      return;
    }

    // Preserve Angular's default console output
    this.logger.error(unwrapped);

    // Build & log a structured, sanitized error report
    this.errorReporting.reportError(unwrapped);
  }

  private unwrapError(error: unknown): unknown {
    // Angular wraps promise rejections with a `.rejection` property
    if (
      error instanceof Error &&
      'rejection' in error &&
      (error as Error & { rejection: unknown }).rejection != null
    ) {
      return (error as Error & { rejection: unknown }).rejection;
    }
    return error;
  }
}
